import { generateLevelPairs, type Level } from '@color-dots/game';

const ENDLESS_KEY = 'color-dots-endless:v1';

export interface EndlessProgress {
  bestStreak: number;
}

export function loadEndlessProgress(): EndlessProgress {
  try {
    const raw = localStorage.getItem(ENDLESS_KEY);
    if (!raw) return { bestStreak: 0 };
    const parsed = JSON.parse(raw) as Partial<EndlessProgress>;
    return { bestStreak: typeof parsed.bestStreak === 'number' ? parsed.bestStreak : 0 };
  } catch {
    return { bestStreak: 0 };
  }
}

export function recordEndlessRun(roundsCleared: number): EndlessProgress {
  const current = loadEndlessProgress();
  const next: EndlessProgress = { bestStreak: Math.max(current.bestStreak, roundsCleared) };
  localStorage.setItem(ENDLESS_KEY, JSON.stringify(next));
  return next;
}

export interface EndlessConfig {
  size: number;
  pairs: number;
  wallCount: number;
}

/**
 * Round 1 starts at medium-tier difficulty (5 pairs) and adds a pair every 3
 * rounds up to a cap of 8 (matching the hardest shipped levels); once pairs are
 * maxed, walls start appearing occasionally instead of the board growing
 * without bound. Endless is meant to be "how long can you keep going", not a
 * ramp that becomes unplayable.
 */
export function endlessConfigForRound(round: number): EndlessConfig {
  const pairs = Math.min(5 + Math.floor((round - 1) / 3), 8);
  const wallCount = pairs >= 8 ? Math.min(Math.floor((round - 1) / 6), 3) : 0;
  return { size: 6, pairs, wallCount };
}

/** `generateLevelPairs` already retries internally (up to 300 shuffled fill
 *  attempts) but can still occasionally come back null by chance even at an
 *  easy config — seen once in CI on the round-1 default (6×6/5 pairs/no
 *  walls), which locally succeeds ~100% of the time over 30 runs. Calling it
 *  again just restarts that same 300-attempt search with a fresh random
 *  order, so a couple of extra tries at the intended config make a real
 *  failure astronomically unlikely before falling back to an easier one. */
function generateWithRetries(
  size: number,
  pairs: number,
  wallCount: number,
  attempts: number,
): ReturnType<typeof generateLevelPairs> {
  for (let i = 0; i < attempts; i++) {
    const generated = generateLevelPairs(size, pairs, wallCount);
    if (generated) return generated;
  }
  return null;
}

export function generateEndlessLevel(round: number): Level {
  const config = endlessConfigForRound(round);
  const generated =
    generateWithRetries(config.size, config.pairs, config.wallCount, 3) ??
    generateWithRetries(config.size, Math.max(4, config.pairs - 1), 0, 2);

  if (!generated) {
    throw new Error(`Failed to generate endless level for round ${round}`);
  }

  return {
    id: `endless-${round}`,
    size: config.size,
    difficulty: 'hard',
    pairs: generated.pairs,
    ...(generated.walls.length > 0 ? { walls: generated.walls } : {}),
  };
}
